import { animate } from 'motion/mini';

type AnimationPreset = {
	keyframes: Record<string, any>;
	options?: Record<string, any>;
};

const DEFAULT_EASING = [0.22, 1, 0.36, 1];

const PRESETS: Record<string, AnimationPreset> = {
	'fade-in': {
		keyframes: { opacity: [0, 1] },
		options: { easing: 'ease-out' },
	},
	'fade-in-up': {
		keyframes: {
			opacity: [0, 1],
			transform: ['translateY(20px)', 'translateY(0)'],
		},
		options: { easing: DEFAULT_EASING },
	},
	'fade-in-down': {
		keyframes: {
			opacity: [0, 1],
			transform: ['translateY(-20px)', 'translateY(0)'],
		},
		options: { easing: DEFAULT_EASING },
	},
	'fade-in-left': {
		keyframes: {
			opacity: [0, 1],
			transform: ['translateX(20px)', 'translateX(0)'],
		},
		options: { easing: DEFAULT_EASING },
	},
	'fade-in-right': {
		keyframes: {
			opacity: [0, 1],
			transform: ['translateX(-20px)', 'translateX(0)'],
		},
		options: { easing: DEFAULT_EASING },
	},
	'scale-up': {
		keyframes: { opacity: [0, 1], scale: [0.95, 1] },
		options: { easing: DEFAULT_EASING },
	},
	// Loops
	breath: {
		keyframes: { scale: [1, 1.1, 1] },
		options: { repeat: Infinity, duration: 2, easing: 'ease-in-out' },
	},
	'up-and-down': {
		keyframes: {
			transform: ['translateY(0)', 'translateY(-10px)', 'translateY(0)'],
		},
		options: { repeat: Infinity, duration: 4, easing: 'ease-in-out' },
	},
	'down-and-up': {
		keyframes: {
			transform: ['translateY(0)', 'translateY(10px)', 'translateY(0)'],
		},
		options: { repeat: Infinity, duration: 4, easing: 'ease-in-out' },
	},
};

/**
 * Common animation runner
 */
const runAnimation = (
	element: HTMLElement,
	type: string,
	baseOptions: { delay?: number; duration?: number },
) => {
	const preset = PRESETS[type] || PRESETS['fade-in-up'];

	// Create options by merging preset and base, but only include duration if it's valid/not default when it's a loop
	const options = { ...preset.options, ...baseOptions };

	return animate(element, preset.keyframes, options as any);
};

/**
 * Lightweight parallax engine
 */
const initParallax = () => {
	const run = () => {
		const elements = document.querySelectorAll('[data-parallax]');
		if (elements.length === 0) return;

		let ticking = false;

		const updateParallax = () => {
			const viewportHeight = window.innerHeight;

			elements.forEach((el) => {
				const element = el as HTMLElement;
				const speedY = parseFloat(
					element.getAttribute('data-parallax-y') ||
						element.getAttribute('data-parallax-speed') ||
						'0.1',
				);
				const speedX = parseFloat(
					element.getAttribute('data-parallax-x') || '0',
				);
				const rect = element.getBoundingClientRect();

				// Only calculate if element is visible or near visible
				if (rect.top < viewportHeight && rect.bottom > 0) {
					// Calculate distance from center of viewport
					const elementCenter = rect.top + rect.height / 2;
					const viewportCenter = viewportHeight / 2;
					const distanceFromCenter = elementCenter - viewportCenter;

					// Apply movement
					if (speedY !== 0) {
						element.style.setProperty(
							'--parallax-y',
							`${distanceFromCenter * speedY}px`,
						);
					}
					if (speedX !== 0) {
						element.style.setProperty(
							'--parallax-x',
							`${distanceFromCenter * speedX}px`,
						);
					}

					if (!element.classList.contains('parallax-ready')) {
						element.classList.add('parallax-ready');
					}
				}
			});

			ticking = false;
		};

		const onScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(updateParallax);
				ticking = true;
			}
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		updateParallax(); // Initial run
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', run);
	} else {
		run();
	}
};

/**
 * Initialize animations for elements with data-animate attribute
 */
export const initAnimations = () => {
	initParallax();
	const observerOptions = {
		rootMargin: '0px 0px 100px 0px',
		threshold: 0.01,
	};

	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				const element = entry.target as HTMLElement;
				const type = element.dataset.animate;
				const loopType = element.dataset.animateLoop;
				const delay = element.dataset.animateDelay
					? parseFloat(element.dataset.animateDelay)
					: 0;
				const durationAttr = element.dataset.animateDuration;
				const duration = durationAttr ? parseFloat(durationAttr) : undefined;

				const baseOptions: { delay?: number; duration?: number } = { delay };
				if (duration !== undefined) baseOptions.duration = duration;

				// Run entry animation if present
				if (type) {
					const animation = runAnimation(element, type, {
						...baseOptions,
						duration: baseOptions.duration || 0.6,
					});

					// If there's a loop, wait for entry animation to finish (approx) or just start it
					if (loopType) {
						animation.finished.then(() => {
							runAnimation(element, loopType, {});
						});
					}
				} else if (loopType) {
					// Only loop animation
					runAnimation(element, loopType, baseOptions);
				}

				observer.unobserve(element);
			}
		});
	}, observerOptions);

	// Observe single elements
	document
		.querySelectorAll('[data-animate], [data-animate-loop]')
		.forEach((el) => observer.observe(el));

	// Observe staggered containers
	document.querySelectorAll('[data-animate-stagger]').forEach((container) => {
		const element = container as HTMLElement;
		const selector = element.dataset.animateStagger || '> *';
		const staggerDelay = parseFloat(
			element.dataset.animateStaggerDelay || '0.1',
		);
		const durationAttr = element.dataset.animateDuration;
		const duration = durationAttr ? parseFloat(durationAttr) : undefined;
		const children = element.querySelectorAll(selector);

		const containerObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					children.forEach((child, index) => {
						const childElement = child as HTMLElement;
						const type =
							childElement.dataset.animate ||
							element.dataset.animateChild ||
							'fade-in-up';
						const loopType =
							childElement.dataset.animateLoop ||
							element.dataset.animateLoopChild;

						const baseOptions: { delay?: number; duration?: number } = {
							delay: index * staggerDelay,
						};
						if (duration !== undefined) baseOptions.duration = duration;

						const animation = runAnimation(childElement, type, {
							...baseOptions,
							duration: baseOptions.duration || 0.6,
						});

						if (loopType) {
							animation.finished.then(() => {
								runAnimation(childElement, loopType, {});
							});
						}
					});
					containerObserver.unobserve(element);
				}
			});
		}, observerOptions);

		containerObserver.observe(element);
	});
};
